import { GeometryExportFormat } from '@egr/xbox/utils/FileFormats';

const HEADER_SIZE = 4;
const DATA_ALIGNMNET = 64;
const CRC_TABLE = prepareCrcTable();

function prepareCrcTable(): Array<number> {
    let i: number;
    let j: number;
    let t: number;

    const table: Array<number> = []; // Uint32Array is actually slower than []

    for (i = 0; i < 256; i++) {
        t = i;
        for (j = 0; j < 8; j++)
            if (t & 1)
                t = (t >>> 1) ^ 0xEDB88320;
            else
                t = t >>> 1;
        table[i] = t;
    }
    return table;
}

class Crc32 {
    private crc: number;

    public constructor() {
        this.crc = -1;
    }

    public append(pData: Uint8Array): void {
        let crc = this.crc | 0;
        for (let offset = 0, len = pData.length | 0; offset < len; offset++)
            crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ pData[offset]) & 0xFF];
        this.crc = crc;
    }

    public get(): number {
        return ~this.crc;
    }
}

class DataHelper {
    public readonly array: Uint8Array;
    public readonly view: DataView;

    public constructor(byteLength: number) {
        const buffer = new ArrayBuffer(byteLength);
        this.array = new Uint8Array(buffer);
        this.view = new DataView(buffer);
    }
}

/**
 * Zipwriter
 */
interface FileDescriptor {
    filename: Uint8Array;
    offset: number;
    extra: number;
    header: Uint8Array;
}

export class UsdZipWriter {
    private files: Array<FileDescriptor> = [];
    private data: Blob = new Blob([], { type: GeometryExportFormat.USDZ.mimetype });

    public addFile(name: string, fileData: ArrayBuffer | string): void {
        const fileBody = fileData instanceof ArrayBuffer ? fileData : getStringBytes(fileData).buffer;

        //convert filename
        const tName = name.trim();

        /* eslint-disable-next-line no-prototype-builtins */
        if (this.files.hasOwnProperty(tName)) {
            throw new Error('Duplicate filename');
        }

        const filename = getStringBytes(this.encodeUTF8(tName));

        const extraSize = computeExtraPaddingSize(this.data.size + 34 + filename.length);

        const { sharedHeader, localHeader } = this.getHeaders(fileBody, filename);

        //add file
        this.files.push({
            filename: filename,
            offset: this.data.size,
            extra: extraSize,
            header: sharedHeader.array
        });

        //write header and body to zip
        this.writeZipData(new Blob([localHeader.array, fileBody]));
    }

    public generateZip(): Blob {
        let centralDictLength = 22, tIndex = 0;

        for (const file of this.files) {
            centralDictLength += 46 + file.filename.length + file.extra + 4;
        }

        const centralDict = new DataHelper(centralDictLength);

        for (const file of this.files) {
            centralDict.view.setUint32(tIndex, 0x504b0102);
            centralDict.array.set(file.header, tIndex + 6);
            centralDict.view.setUint16(tIndex + 32, 0, true);

            centralDict.view.setUint32(tIndex + 42, file.offset, true);
            centralDict.array.set(file.filename, tIndex + 46);
            centralDict.view.setUint16(tIndex + 46 + file.filename.length, 0x1986, true);
            centralDict.view.setUint16(tIndex + 48 + file.filename.length, file.extra, true);
            tIndex += 50 + file.filename.length + file.extra;
        }
        centralDict.view.setUint32(tIndex, 0x504b0506);
        centralDict.view.setUint16(tIndex + 8, this.files.length, true);
        centralDict.view.setUint16(tIndex + 10, this.files.length, true);
        centralDict.view.setUint32(tIndex + 12, centralDictLength, true);
        centralDict.view.setUint32(tIndex + 16, this.data.size, true);

        this.writeZipData(new Blob([centralDict.array]));

        return this.data;
    }

    private writeZipData(pBlob: Blob): void {
        this.data = new Blob([this.data, pBlob], { type: GeometryExportFormat.USDZ.mimetype });
    }

    private encodeUTF8(string: string): string {
        return unescape(encodeURIComponent(string));
    }

    private getHeaders(
        fileBody: ArrayBufferLike,
        filename: Uint8Array,
    ): { sharedHeader: DataHelper, localHeader: DataHelper } {
        const crc = new Crc32();
        crc.append(new Uint8Array(fileBody));

        const fileBodyLength = fileBody.byteLength;

        const date = new Date();

        const extraSize = computeExtraPaddingSize(this.data.size + 34 + filename.length);

        const sharedHeader = new DataHelper(26);

        sharedHeader.view.setUint16(0, 0x0a, true);
        sharedHeader.view.setUint16(6, (((date.getHours() << 6) | date.getMinutes()) << 5) | date.getSeconds() / 2, true);
        sharedHeader.view.setUint16(8, ((((date.getFullYear() - 1980) << 4) | (date.getMonth() + 1)) << 5) | date.getDate(), true);
        sharedHeader.view.setUint32(10, crc.get(), true);
        sharedHeader.view.setUint32(14, fileBodyLength, true);
        sharedHeader.view.setUint32(18, fileBodyLength, true);
        sharedHeader.view.setUint16(22, filename.length, true);
        sharedHeader.view.setUint16(24, extraSize + 4, true);

        const localHeader = new DataHelper(30 + filename.length + extraSize + 4);

        localHeader.view.setUint32(0, 0x504b0304);
        localHeader.array.set(sharedHeader.array, 4);
        localHeader.array.set(filename, 30);
        localHeader.view.setUint16(30 + filename.length, 0x1986, true);
        localHeader.view.setUint16(32 + filename.length, extraSize, true);

        return { sharedHeader, localHeader };
    }
}

function computeExtraPaddingSize(pOffset: number): number {
    let requiredPadding: number = DATA_ALIGNMNET - (pOffset % DATA_ALIGNMNET);

    if (requiredPadding == DATA_ALIGNMNET) {
        requiredPadding = 0;
    } else if (requiredPadding < HEADER_SIZE) {
        requiredPadding += DATA_ALIGNMNET;
    }

    return requiredPadding;
}

function getStringBytes(str: string): Uint8Array {
    const bytes = new Uint8Array(str.length);

    for (let i = 0; i < str.length; i++) {
        bytes[i] = str.charCodeAt(i);
    }

    return bytes;
}